import { Box, Text } from "@chakra-ui/react";
import { forwardRef } from "react";

/**
 * CheckboxCard — a chip-styled multi-select option. Active state lifts the
 * background to a soft brand wash and outlines the tile with Electric Blue;
 * inactive sits flat against the surface with a hairline border.
 *
 * Has a built-in `locked` "Always on" variant for fields that must always be
 * checked — e.g. the Profile page in the Portal config can't be toggled off.
 *
 * Theme-aware via `--bg-primary`, `--border-default`, `--text-*` tokens, and
 * the brand-primary CSS variable (Electric Blue).
 */
export interface CheckboxCardProps {
  label: string;
  checked: boolean;
  onChange?: (next: boolean) => void;
  /**
   * When true the card displays as permanently-checked, ignores clicks, and
   * appends an "Always on" mono-uppercase tag on the right.
   */
  locked?: boolean;
  disabled?: boolean;
  alwaysOnLabel?: string;
}

export const CheckboxCard = forwardRef<HTMLButtonElement, CheckboxCardProps>(
  function CheckboxCard(
    { label, checked, onChange, locked, disabled, alwaysOnLabel = "Always on" },
    ref,
  ) {
    const isOn = locked || checked;
    const isInteractive = !locked && !disabled;

    return (
      <button
        ref={ref}
        type="button"
        onClick={() => isInteractive && onChange?.(!checked)}
        disabled={!isInteractive}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          width: "100%",
          background: isOn ? "rgba(59, 130, 246, 0.06)" : "var(--bg-primary)",
          border:
            "1px solid " +
            (isOn ? "var(--brand-primary, #3B82F6)" : "var(--border-default)"),
          borderRadius: 10,
          cursor: isInteractive ? "pointer" : "not-allowed",
          opacity: disabled && !locked ? 0.55 : 1,
          textAlign: "left",
          color: "var(--text-primary)",
          fontFamily: "var(--font-body)",
          transition: "background-color 0.15s, border-color 0.15s",
        }}
      >
        <Box
          as="span"
          width="18px"
          height="18px"
          borderRadius="5px"
          bg={isOn ? "var(--brand-primary, #3B82F6)" : "transparent"}
          borderWidth="1.5px"
          borderStyle="solid"
          borderColor={
            isOn
              ? "var(--brand-primary, #3B82F6)"
              : "var(--text-tertiary-color, #88898E)"
          }
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          {isOn && (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </Box>
        <Text
          flex={1}
          fontSize="sm"
          fontWeight={500}
          color="var(--text-primary)"
          m={0}
        >
          {label}
        </Text>
        {locked && (
          <Text
            fontFamily="var(--font-mono)"
            fontSize="10px"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color="var(--text-tertiary-color, #88898E)"
            m={0}
          >
            {alwaysOnLabel}
          </Text>
        )}
      </button>
    );
  },
);

export default CheckboxCard;
